import { Currency } from '../enum/currency'
import { Factory } from '../interface/factory'
import { Identifiable } from '../interface/identifiable'
import { Modifiable } from '../interface/modifiable'
import { rememberPayload } from '../../_utils/fcdc-v2'

export class ConversionRates implements Identifiable, Modifiable {
  id: string
  timeStamp: Date
  modified: Date
  created: Date
  currency: Currency
  // rates are now always multipliers of the base rate, which is always 1
  rates: [Currency, number][]

  constructor(json: any) {
    this.id = String(json.id || crypto.randomUUID())
    this.modified = json.modified ? new Date(json.modified) : new Date()
    this.created = json.created ? new Date(json.created) : new Date()

    if (!json.currency) {
      console.error('Invalid or missing currency:', json.currency)
      throw new Error(
        `Invalid currency: ${json.currency}. Must be one of: ${Object.values(Currency).join(', ')}`
      )
    }
    this.currency = json.currency

    if (json.timeStamp instanceof Date) {
      this.timeStamp = json.timeStamp
    } else if (json.timeStamp) {
      const date = new Date(json.timeStamp)
      if (isNaN(date.getTime())) {
        console.error('Invalid timeStamp provided:', json.timeStamp)
        this.timeStamp = new Date()
      } else {
        this.timeStamp = date
      }
    } else {
      this.timeStamp = new Date()
    }

    this.rates = []

    if (json.rates) {
      if (json.rates instanceof Map) {
        this.rates = json.rates
      } else if (Array.isArray(json.rates)) {
        json.rates.forEach(([currency, rate]: [Currency, number]) => {
          this.rates.push([currency, rate])
        })
      } else if (typeof json.rates === 'object') {
        Object.entries(json.rates).forEach(([currency, rate]) => {
          this.rates.push([currency as Currency, rate as number])
        })
      } else if (json.rates instanceof Array) {
        this.rates = json.rates
      }
    }

    rememberPayload(this, json)
  }

  getRateForCurrency(currency: Currency): number | undefined {
    if (currency === Currency.EUR) {
      return 1
    }
    const index = this.rates.findIndex(([c]) => c === currency)
    if (index === -1) {
      throw new Error(`Rate for currency ${currency} not found`)
    }
    return this.rates[index][1]
  }

  static getFactory(): Factory<ConversionRates> {
    return new (class implements Factory<ConversionRates> {
      make(json: any): ConversionRates {
        return new ConversionRates(json)
      }

      getTableName(): string {
        return 'conversion_rates'
      }
    })()
  }

  getUrl(currency: Currency): string {
    if (this.currency === currency) {
      return 'https://api.frankfurter.app/latest'
    }

    if (!this.timeStamp || isNaN(this.timeStamp.getTime())) {
      console.error('Invalid timeStamp in ConversionRates:', this.timeStamp)
      this.timeStamp = new Date()
    }

    try {
      const formattedDate = this.timeStamp.toISOString().split('T')[0]
      const url = `https://api.frankfurter.app/${formattedDate}?from=${this.currency}&to=${currency}`
      return url
    } catch (error) {
      console.error('Error formatting date in getUrl:', error)
      const currentDate = new Date().toISOString().split('T')[0]
      const url = `https://api.frankfurter.app/${currentDate}?from=${this.currency}&to=${currency}`
      return url
    }
  }
}
